import { mapActions, mapGetters } from 'vuex';
import { debounce } from 'lodash';
import { ACTIONS } from '@/constants/softPhone/constants';
import { TERMINAL_TYPES } from '@/constants/terminalTypes';
import { AGENT_STATE } from '@/constants/agent/constants';
import AgentService from '@/services/agentService';

let isBackofficeDueToDirectCall = false;

export default {
  name: 'EcsSoftPhoneMixin',

  sockets: {
    AGENT_STATE_CHANGED_EVENT({ stateId, agentId }) {
      if (agentId !== this.loggedInAgent.id) {
        return;
      }
      // If the agent is in backoffice state due to a direct call, and the agent state changes to a different state,
      // then set the 'isBackofficeDueToDirectCall' false to prevent going back to ready state when the direct call ends.
      if (isBackofficeDueToDirectCall && stateId !== AGENT_STATE.backoffice) {
        isBackofficeDueToDirectCall = false;
      }
    }
  },

  computed: {
    ...mapGetters('AGENT', [
      'loggedInAgent',
      'isSoftPhoneRegistered',
      'isRegisteringSoftPhone',
      'softPhoneRegistrationErrors',
      'agentState'
    ]),

    ...mapGetters('SETTINGS', ['isOfflineModeEnabled', 'userSoftPhoneSettings']),

    ...mapGetters('SYSTEM', ['systemSoftPhoneSettings']),

    enableWebRTCSoftPhone() {
      return this.systemSoftPhoneSettings && this.systemSoftPhoneSettings.enableWebRTCSoftphone;
    },

    isHPBXPhone() {
      return (
        this.loggedInAgent &&
        this.loggedInAgent.currentExtension &&
        this.loggedInAgent.currentExtension.terminalType === TERMINAL_TYPES.HPBX_PHONE
      );
    },

    isInternalPhone() {
      return (
        this.loggedInAgent &&
        this.loggedInAgent.currentExtension &&
        this.loggedInAgent.currentExtension.terminalType === TERMINAL_TYPES.INTERNAL_PHONE
      );
    },

    useBrowserToAnswer() {
      return this.isInternalPhone && this.enableWebRTCSoftPhone;
    },

    isSoftPhoneReadyToUse() {
      return this.isSoftPhoneRegistered && this.useBrowserToAnswer;
    },

    isSoftPhoneFeatureEnabled() {
      return this.enableWebRTCSoftPhone;
    },

    isSoftPhoneFeatureEnabledAndReadyToUse() {
      return this.isSoftPhoneFeatureEnabled && this.isSoftPhoneReadyToUse;
    },

    shouldRegisterSoftPhone() {
      return this.useBrowserToAnswer && !this.agentState.isOffline;
    }
  },

  watch: {
    useBrowserToAnswer() {
      if (this.useBrowserToAnswer) {
        this.initializeSoftPhone();
      }
    },
    shouldRegisterSoftPhone: debounce(function() {
      if (this.shouldRegisterSoftPhone === undefined) {
        return;
      }

      if (this.shouldRegisterSoftPhone) {
        this.registerSoftPhone();
      } else if (this.isSoftPhoneRegistered) {
        this.unregisterSoftPhone();
      }
    }, 100)
  },

  methods: {
    ...mapActions('AGENT', [
      'updateSoftPhoneRegisteredState',
      'updateIsRegisteringSoftPhone',
      'updateSoftPhoneRegistrationErrors'
    ]),

    ...mapActions('INTERACTIONS', [
      'removePendingInteraction', // Decline
      'movePendingInteractionToActive', // Answer
      'moveActiveInteractionToHistory' // end
    ]),

    initializeSoftPhone() {
      this.softPhoneAction(ACTIONS.initialize);
    },

    registerSoftPhone(isOffline = false) {
      return new Promise((resolve, reject) => {
        if (
          !this.useBrowserToAnswer ||
          (this.useBrowserToAnswer && this.isSoftPhoneRegistered) ||
          (this.useBrowserToAnswer && this.isRegisteringSoftPhone)
        ) {
          if (isOffline) {
            this.softPhoneAction(ACTIONS.unregister);
          }
          resolve();
        } else {
          this.softPhoneAction(ACTIONS.register, resolve);
        }
      });
    },

    unregisterSoftPhone() {
      this.softPhoneAction(ACTIONS.unregister);
    },

    destroySoftPhone() {
      this.softPhoneAction(ACTIONS.destroy);
    },

    softPhoneAction(action, callback) {
      this.$root.$emit('soft-phone-action', { action, callback });
    },

    /**
     * Accept direct call interaction.
     * @param {EcsInteraction} interaction
     **/
    acceptDirectCallInteraction(interaction) {
      const currentAgentState = this.agentState;
      const answer = () => {
        this.$root.$emit('soft-phone-action', {
          action: ACTIONS.answer,
          callback: () => {
            this.createDirectCallInteractionWindow(interaction);
            AgentService.markAsOnDirectCall(true);
          }
        });
      };

      if (currentAgentState.isReady) {
        this.$root.$emit('submit-agent-state-change', {
          id: AGENT_STATE.backoffice,
          onSuccess: () => {
            answer();
            isBackofficeDueToDirectCall = true;
          },
          onError: () => this.notifyDirectCallInteractionFailure(interaction)
        });
      } else {
        answer();
      }
    },

    createDirectCallInteractionWindow(interaction) {
      this.$router
        .push('calls')
        .catch(() => {})
        .finally(() => {
          interaction.timing.directCallAnsweredAt = Date.now();
          this.movePendingInteractionToActive(interaction);
        });
    },

    notifyDirectCallInteractionFailure(interaction) {
      console.log('Direct call interaction failure', interaction);
    },

    /**
     * Decline direct call interaction.
     * @param {EcsInteraction} interaction
     **/
    declineDirectCallInteraction(interaction) {
      this.$root.$emit('soft-phone-action', {
        action: ACTIONS.decline,
        callback: () => {
          this.removePendingInteraction(interaction);
          AgentService.markAsOnDirectCall(false);
        }
      });
    },

    endPendingDirectCallInteraction(interaction) {
      this.$root.$emit('soft-phone-action', {
        action: ACTIONS.hangup,
        callback: () => {
          this.removePendingInteraction(interaction);
          AgentService.markAsOnDirectCall(false);
        }
      });
    },

    endDirectCallInteraction(interaction) {
      this.$root.$emit('soft-phone-action', {
        action: ACTIONS.hangup,
        callback: async () => {
          await AgentService.markAsOnDirectCall(false);
          if (isBackofficeDueToDirectCall) {
            isBackofficeDueToDirectCall = false;
            this.$root.$emit('submit-agent-state-change', { id: AGENT_STATE.ready });
          }
          this.moveActiveInteractionToHistory(interaction);
        }
      });
    },

    holdDirectCallInteraction() {
      this.$root.$emit('soft-phone-action', { action: ACTIONS.hold });
    },

    resumeDirectCallInteraction() {
      this.$root.$emit('soft-phone-action', { action: ACTIONS.unhold });
    },

    dtmfDirectCallInteraction(digit) {
      this.$root.$emit('soft-phone-action', { action: ACTIONS.dtmf, digit: digit + '', inband: true });
    }
  }
};
